<template>
  <div class="upload-path">
    <shell>
      <template #appbar>
        <app-bar
          :show-back="screen !== 'done'"
          :subtitle="subtitle"
          :step-label="stepLabel"
          @back="goBack"
        />
      </template>

      <template v-if="railActive" #rail>
        <progress-rail :steps="RAIL_STEPS" :active-step="railActive" />
      </template>
      <template v-if="railActive" #sidebar>
        <case-file-rail
          :steps="RAIL_STEPS"
          :active-step="railActive"
          :summaries="stepSummaries"
          @jump="jumpToStep"
        />
      </template>

      <template v-if="screen === 'lookup'">
        <div class="mc-card-soft mc-mb20">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            class="mc-info-icon"
          >
            <path
              d="M6 3h9l3 3v15a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z"
            />
            <path d="M14 3v4h4" />
          </svg>
          <div class="mc-info-copy">
            <b>Motor Accident Report Form</b> — download it, fill every field,
            then upload the completed copy on the next step.
            <div class="mc-tmpl-row">
              <button
                type="button"
                class="mc-btn mc-btn--sec mc-tmpl-btn"
                @click="downloadTemplate"
              >
                Download template (PDF)
              </button>
              <span v-if="templateDownloaded" class="mc-tmpl-ok"
                >✓ Downloaded</span
              >
            </div>
          </div>
        </div>

        <lookup-step :value="S" :lookup-failed="lookupFailed" @input="merge" />
      </template>

      <documents-step
        v-else-if="screen === 'upload'"
        :docs="DOCS"
        :captured="captured"
        @capture="onCapture"
        @remove-capture="onRemoveCapture"
        @capture-error="(key, msg) => showToast(msg)"
      />

      <review-sign-step
        v-else-if="screen === 'review'"
        :groups="reviewGroups"
        :consent="S.consent"
        :require-signature="false"
        @edit-group="(key) => (screen = key)"
        @update-consent="(v) => (S.consent = v)"
      />

      <done-step
        v-else-if="screen === 'done'"
        :reference="S.ref"
        :phone="S.phone"
      />

      <template #bar>
        <bottom-bar>
          <template v-if="screen === 'done'">
            <button
              type="button"
              class="mc-btn mc-btn--sec"
              @click="showToast('Your claim summary would download here')"
            >
              Save summary
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              @click="$emit('back')"
            >
              Done
            </button>
          </template>
          <template v-else>
            <button
              v-if="screen !== 'lookup'"
              type="button"
              class="mc-btn mc-btn--sec"
              @click="goBack"
            >
              Back
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              :disabled="!canContinue || lookingUp || submitting"
              @click="onPrimary"
            >
              {{ primaryLabel }}
            </button>
          </template>
        </bottom-bar>
      </template>

      <template #toast><toast ref="toast" /></template>
    </shell>
  </div>
</template>

<script>
import Shell from '../motor-claim/shell/Shell.vue'
import AppBar from '../motor-claim/shell/AppBar.vue'
import ProgressRail from '../motor-claim/shell/ProgressRail.vue'
import CaseFileRail from '../motor-claim/shell/CaseFileRail.vue'
import BottomBar from '../motor-claim/shell/BottomBar.vue'
import Toast from '../motor-claim/shell/Toast.vue'
import LookupStep from '../motor-claim/steps/LookupStep.vue'
import DocumentsStep from '../motor-claim/shared/DocumentsStep.vue'
import ReviewSignStep from '../motor-claim/shared/ReviewSignStep.vue'
import DoneStep from '../motor-claim/shared/DoneStep.vue'
import {
  getTemplateUrl,
  initiateClaim,
  uploadClaimAttachment,
} from '@/services/claim-service'
import { DRAFT_KEYS, saveDraft, loadDraft } from '@/utils/claimDraft'

const SCREEN_ORDER = ['lookup', 'upload', 'review']
const RAIL_STEPS = ['Confirm vehicle', 'Attach documents', 'Review']
const RAIL_STEP_FOR = { lookup: 1, upload: 2, review: 3, done: 0 }

// The completed report form is sent as the submission's primary file (not a
// generic attachment) — the backend reads the claim body from it server-side.
// The rest go through the same capture grid as the online-form journey.
const DOCS = [
  {
    key: 'completedForm',
    label: 'Completed Report Form',
    hint: 'The filled Motor Accident Report Form',
    required: true,
    accept: '.pdf,.doc,.docx,image/*',
  },
  { key: 'licence', label: 'Driving licence', required: true },
  { key: 'idcopy', label: 'National ID / Passport', required: true },
  { key: 'abstract', label: 'Police abstract / OB', required: false },
  { key: 'logbook', label: 'Logbook', required: false },
]

const blankState = () => ({
  onBehalf: false,
  reg: '',
  phone: '',
  docs: {},
  consent: false,
  ref: null,
  // Set once /initiate-claim succeeds; passed back on submit so it merges
  // into that same DRAFT instead of staging a second, orphaned record.
  claimId: null,
})

function lookupSummary(S) {
  if (!S.reg) return ''
  return S.phone ? `${S.reg} · ${S.phone}` : S.reg
}

// Paper-upload journey. Shares the online-form journey's shell, lookup step,
// document capture grid and review/sign/done screens (see MotorClaimForm.vue)
// so both paths look and behave the same end to end, including owning their
// own success screen rather than bouncing back through SubmitClaim.vue.
export default {
  name: 'UploadPath',
  components: {
    Shell,
    AppBar,
    ProgressRail,
    CaseFileRail,
    BottomBar,
    Toast,
    LookupStep,
    DocumentsStep,
    ReviewSignStep,
    DoneStep,
  },
  emits: ['back', 'submit'],
  data() {
    return {
      screen: 'lookup',
      S: blankState(),
      RAIL_STEPS,
      DOCS,
      lookingUp: false,
      lookupFailed: false,
      submitting: false,
      templateDownloaded: false,
      // the report file itself — kept outside S so it's never serialised into
      // the localStorage draft (a raw File doesn't survive JSON round-tripping)
      completedForm: null,
    }
  },
  computed: {
    origin() {
      return 'UPLOAD'
    },
    railActive() {
      return RAIL_STEP_FOR[this.screen]
    },
    stepLabel() {
      const n = RAIL_STEP_FOR[this.screen]
      return n ? `Step ${n} of 3` : ''
    },
    subtitle() {
      if (this.screen === 'done') return `Claim ${this.S.ref || ''}`
      return 'Motor claim · Upload filled form'
    },
    captured() {
      return this.completedForm
        ? { ...this.S.docs, completedForm: this.completedForm }
        : this.S.docs
    },
    docsOk() {
      return this.DOCS.filter((d) => d.required).every((d) =>
        d.key === 'completedForm'
          ? !!this.completedForm
          : this.S.docs[d.key] && !this.S.docs[d.key].uploading
      )
    },
    docsCount() {
      return Object.keys(this.S.docs).length + (this.completedForm ? 1 : 0)
    },
    stepSummaries() {
      return {
        1: lookupSummary(this.S),
        2: this.docsCount
          ? `${this.docsCount} item${this.docsCount > 1 ? 's' : ''} attached`
          : '',
      }
    },
    reviewGroups() {
      const S = this.S
      return [
        {
          key: 'lookup',
          title: 'Vehicle',
          rows: [
            ['Registration number', S.reg],
            ['Contact number', S.phone],
          ],
        },
        {
          key: 'upload',
          title: 'Documents submitted',
          rows: this.DOCS.map((d) => [
            d.label,
            this.captured[d.key] ? this.captured[d.key].name : '',
          ]),
        },
      ]
    },
    canContinue() {
      const S = this.S
      switch (this.screen) {
        case 'lookup':
          return (
            S.reg.trim().length >= 5 &&
            /\d{9,}/.test(S.phone.replace(/\D/g, ''))
          )
        case 'upload':
          return this.docsOk
        case 'review':
          return S.consent
        default:
          return true
      }
    },
    primaryLabel() {
      if (this.screen === 'lookup') {
        return this.lookingUp ? 'Looking up…' : 'Continue'
      }
      if (this.submitting) return 'Submitting…'
      if (this.screen === 'upload') return 'Continue to review'
      if (this.screen === 'review') return 'Submit claim'
      return 'Continue'
    },
  },
  watch: {
    S: {
      deep: true,
      handler() {
        saveDraft(DRAFT_KEYS.upload, { S: this.S })
      },
    },
  },
  created() {
    // restore field values only — the journey always re-opens on the lookup
    // gate so the vehicle is re-verified, and the report file is never persisted
    const draft = loadDraft(DRAFT_KEYS.upload)
    if (draft && draft.S) this.S = { ...blankState(), ...draft.S }
  },
  methods: {
    merge(patch) {
      Object.assign(this.S, patch)
    },
    showToast(message) {
      if (this.$refs.toast) this.$refs.toast.show(message)
    },
    jumpToStep(stepNumber) {
      const target = SCREEN_ORDER[stepNumber - 1]
      if (target) this.screen = target
    },
    goBack() {
      const i = SCREEN_ORDER.indexOf(this.screen)
      if (i <= 0) {
        this.$emit('back')
        return
      }
      this.screen = SCREEN_ORDER[i - 1]
    },
    downloadTemplate() {
      window.open(getTemplateUrl(), '_blank')
      this.templateDownloaded = true
    },
    onPrimary() {
      if (this.screen === 'lookup') {
        this.submitLookup()
        return
      }
      if (this.screen === 'review') {
        this.submit()
        return
      }
      const i = SCREEN_ORDER.indexOf(this.screen)
      this.screen = SCREEN_ORDER[i + 1]
    },
    async submitLookup() {
      this.lookingUp = true
      this.lookupFailed = false
      try {
        const { data } = await initiateClaim(
          this.S.reg.trim(),
          this.S.phone.trim()
        )
        if (!data || !data.success) {
          // LookupStep's card owns the copy; no toast, so it stays onscreen
          this.lookupFailed = true
          return
        }
        // the claim reference is assigned here, at initiate-claim — shown
        // later on the done screen, not whatever id the final submit returns
        this.S.ref = (data.data && data.data.claimReference) || ''
        this.S.claimId = data.data && data.data.id
        this.showToast('Policy verified')
        this.screen = 'upload'
      } finally {
        this.lookingUp = false
      }
    },
    async onCapture(key, file) {
      if (key === 'completedForm') {
        this.completedForm = { name: file.name, file }
        return
      }
      this.S.docs = {
        ...this.S.docs,
        [key]: { name: file.name, uploading: true },
      }
      try {
        const { data } = await uploadClaimAttachment(file, this.S.reg, key)
        if (!data || !data.success) throw new Error(data && data.msg)
        this.S.docs = {
          ...this.S.docs,
          [key]: { name: file.name, type: data.data.type, url: data.data.url },
        }
      } finally {
        if (this.S.docs[key] && this.S.docs[key].uploading) {
          const docs = { ...this.S.docs }
          delete docs[key]
          this.S.docs = docs
        }
      }
    },
    onRemoveCapture(key) {
      if (key === 'completedForm') {
        this.completedForm = null
        return
      }
      const docs = { ...this.S.docs }
      delete docs[key]
      this.S.docs = docs
    },
    // Submits, then reports the outcome back through a callback (Vue events
    // have no return value) so this component can own its own review/done
    // screens instead of bouncing back through SubmitClaim.vue's — the same
    // self-contained shape as MotorClaimForm's finishClaim.
    submit() {
      const attachments = Object.values(this.S.docs).map((d) => ({
        type: d.type,
        url: d.url,
      }))
      this.submitting = true
      this.$emit(
        'submit',
        {
          claimId: this.S.claimId,
          file: this.completedForm && this.completedForm.file,
          vehicleRegistrationNumber: this.S.reg,
          insuredMobileNumber: this.S.phone,
          attachments,
          origin: this.origin,
        },
        (result) => {
          this.submitting = false
          if (!result || !result.success) {
            this.showToast(
              (result && result.msg) ||
                'Could not submit your claim. Please try again.'
            )
            return
          }
          // S.ref was already set from initiate-claim's claim reference
          this.screen = 'done'
        }
      )
    },
  },
}
</script>

<style scoped>
.upload-path {
  height: 100%;
}

.mc-mb20 {
  margin-bottom: 20px;
}

.mc-card-soft {
  display: flex;
  gap: 11px;
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  padding: 16px;
}

.mc-info-icon {
  color: #0052b3;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mc-info-copy {
  font-size: 12.5px;
  color: #002247;
  line-height: 1.5;
}

.mc-tmpl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.mc-tmpl-btn {
  flex: 0 0 auto;
  padding: 8px 14px;
  font-size: 12.5px;
}

.mc-tmpl-ok {
  color: #1f7a3c;
  font-weight: 700;
  font-size: 12px;
}
</style>
